import isEmpty from "lodash/isEmpty";
import intersection from "lodash/intersection";

import capitalize from "lodash/capitalize";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "@stadium/ui/src/components/ModalNew";
import {
  ModalBody,
  ModalContent,
  ModalDialog,
  ModalHeader,
  ModalFooter,
  CloseButton,
} from "@stadium/ui/src/components/ModalNew/utils";

import CheckboxInput from "@stadium/ui/src/components/form/CheckboxInput";
import Label from "@stadium/ui/src/components/form/Label";
import { InternalButton } from "@components/shared/buttons";
import Multimedia from "@sharedComponents/multimedia";
import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";
import EditPriceRangeModal from "@components/modals/edit-price-range-modal";

import { getMenu } from "@selectors/menu";
import { getNestedOrderedMenuItems, replaceCloudinaryDimensions } from "@helpers/menu";
import useUpdateDummyStore from "@hooks/store/use-update-dummy-store";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { useGuideTourContext } from "@contexts/guide-tour";
import { useJuneSoEventTracking } from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";

import { getDummyStorePriceRange, getIsPricelessShop } from "@selectors/store";
import { setMenu } from "@slices/menu";

import sampleImage from "@images/no-image.jpg";

import { ALL_PRODUCTS } from "@constants/routes";
import {
  MENU_CATALOG_SELECTION,
  PRICELESS_SHOP,
  SHOP_CREATION,
  SHOP_CREATION_FLOW,
} from "@constants/june-so";
import { DEFAULT_CURRENCY_CODE } from "@stadium/constants";

import styles from "./main.module.scss";

const DummyStoreCategories = ({ isModal = false, closeModal }) => {
  const dispatch = useDispatch();
  const menu = useSelector(getMenu);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const defaultPrices = useSelector(getDummyStorePriceRange);
  const { minPrice: defaultMinPrice, maxPrice: defaultMaxPrice } = defaultPrices;
  const { trackEvent } = useJuneSoEventTracking({ services: SHOP_CREATION_FLOW });

  const { allChildren: menuItems, allChildTaxonIds, id: menuId } = menu;

  const [selectedTaxonIds, setSelectedTaxonIds] = useState([]);
  const [showEditPriceRangeModal, setShowEditPriceRangeModal] = useState(false);
  const [priceRange, setPriceRange] = useState({ minPrice: "", maxPrice: "" });
  const { minPrice, maxPrice } = priceRange;

  const { dummyStoreNumber } = useCreateShopContext();
  const { updateDummyStore, loading } = useUpdateDummyStore(dummyStoreNumber);

  const { guideTourSteps } = useGuideTourContext();

  const { step3 = {} } = guideTourSteps;

  const catalogOptions = useMemo(
    () =>
      getNestedOrderedMenuItems({
        menuItems: menuItems || [],
        includeDisabledMenuItems: true,
        isNewShopCreationFlow: true,
      })?.children,
    [menuItems],
  );

  const getIsMenuItemSelected = useCallback(
    (menuItem) => !isEmpty(intersection(menuItem.allChildTaxonIds, selectedTaxonIds)),
    [selectedTaxonIds],
  );

  const isMultipleCategoriesSelected = useMemo(() => {
    if (isModal) return true;
    const selectedCategoriesCount = catalogOptions.reduce(
      (acc, menuItem) => acc + +getIsMenuItemSelected(menuItem),
      0,
    );

    return selectedCategoriesCount > 1;
  }, [selectedTaxonIds, catalogOptions]);

  const handlePriceRangeSave = () => {
    updateDummyStore({
      data: { minPrice: +minPrice, maxPrice: +maxPrice },
      successMsg: "Price range has been updated.",
      successTitle: "Success!",
      showSuccessMessage: true,
      successCb: () => {
        setShowEditPriceRangeModal(false);
      },
    });
  };

  const handleEditPriceRangeModalClose = () => setShowEditPriceRangeModal(false);
  const handleEditPriceRangeModalOpen = () => setShowEditPriceRangeModal(true);

  useEffect(() => {
    if (!isEmpty(catalogOptions)) setSelectedTaxonIds(allChildTaxonIds);
  }, [catalogOptions]);
  useEffect(() => {
    setPriceRange({
      minPrice: defaultMinPrice?.toString() || "",
      maxPrice: defaultMaxPrice?.toString() || "",
    });
  }, [defaultMaxPrice, defaultMinPrice]);

  const updateMenuItemsLocally = (toggledTaxonIds) => {
    const updatedMenuItems = [...menuItems].map((menuItem) => ({
      ...menuItem,
      ...(!isEmpty(menuItem.linkedTaxonResources) && {
        isDisabled: isEmpty(
          intersection(
            toggledTaxonIds,
            menuItem.linkedTaxonResources.map(({ id }) => +id),
          ),
        ),
      }),
    }));

    const orderedMenuItem = getNestedOrderedMenuItems({
      menuItems: updatedMenuItems,
      isNewShopCreationFlow: true,
    });

    dispatch(setMenu({ ...menu, ...orderedMenuItem, id: menuId }));
  };

  const onChangeCheckbox = (menuItem) => {
    const childItemIds = menuItem.allChildTaxonIds;

    const isCategoryEnabled = childItemIds.some((taxon) => selectedTaxonIds.includes(taxon));

    let updatedCategories;

    if (isCategoryEnabled) {
      updatedCategories = selectedTaxonIds.filter((taxon) => !childItemIds.includes(taxon));
    } else {
      updatedCategories = [...selectedTaxonIds, ...childItemIds];
    }

    setSelectedTaxonIds(updatedCategories);

    if (!isModal) {
      saveShopCategories(updatedCategories);
    }
  };

  const saveShopCategories = (updatedCategories = null) => {
    const taxonIds = (updatedCategories || selectedTaxonIds).map((taxonId) => +taxonId);
    updateDummyStore({
      data: { taxonIds },
      successMsg: "Shop Categories have been updated.",
      showSuccessMessage: isModal,
      successCb: () => {
        isModal && closeModal();
        updateMenuItemsLocally(taxonIds);
      },
    });
  };

  const renderCatalogOption = (menuItem) => {
    const { id, name, attachment, code, productsImage } = menuItem;
    const checked = getIsMenuItemSelected(menuItem),
      nameCapital = name
        ?.split(" ")
        .map((wrd) => capitalize(wrd))
        .join(" "),
      isDisabled = !isMultipleCategoriesSelected && checked;

    if (code === ALL_PRODUCTS) return null;

    const categoryImage =
      productsImage ||
      replaceCloudinaryDimensions({
        url: attachment,
        height: 56,
        width: 56,
      }) ||
      attachment ||
      sampleImage;

    const renderCheckboxInput = () => (
      <div className={styles.contentHolder}>
        <CheckboxInput
          value="value"
          name="name"
          onChange={() => onChangeCheckbox(menuItem)}
          checked={checked}
          disabled={isDisabled}
          id={id}
          label={
            <Label styles={styles} iconType="check" className={styles.inputLabel}>
              <div className={styles.name}>{nameCapital}</div>
              <div className={styles.imageBlock}>
                <Multimedia
                  type="image"
                  src={categoryImage}
                  isNextImage={false}
                  height={56}
                  width={56}
                />
              </div>
            </Label>
          }
        />
      </div>
    );

    const renderDisabledCheckbox = () => (
      <ToolTip arrow isLightTheme content="Atleast 1 Category must be selected." placement="top">
        {renderCheckboxInput()}
      </ToolTip>
    );

    return (
      <div className={styles.categoryListItem}>
        {isDisabled ? renderDisabledCheckbox() : renderCheckboxInput()}
      </div>
    );
  };

  const renderCategoriesHeader = () => (
    <div className={styles.categoryMessageBlock}>
      <h2 className={styles.title} ref={step3.ref}>
        Choose categories for your shop
      </h2>
      {isPricelessShop &&
      ((catalogOptions?.length === 1 && catalogOptions[0].code === ALL_PRODUCTS) ||
        catalogOptions?.length === 0) ? (
        <div className={styles.rangeMessageHolder}>
          <p className={styles.rangeMessage}>
            <span className={styles.iconAlert}></span>
            No products found in this price range in the selected country. Adjust your price range
            to see more products in your shop.
          </p>
        </div>
      ) : (
        <p className={styles.infoText}>
          Choose categories for recipients to shop from—you can also browse products from the
          categories above. After saving your shop, you'll be able to enable/disable products within
          your catalog.
        </p>
      )}
    </div>
  );

  const renderPricelessCategoriesHeader = () => (
    <div className={styles.categoriesMessageHolder}>
      {renderCategoriesHeader()}
      <div className={styles.editPriceRangeBlock}>
        <div className={styles.editPriceRangeBlockContent}>
          <div className={styles.editPriceRangeBlockContentTitle}>Price range</div>
          <div
            className={styles.editPriceRangeBlockContentValue}
          >{`${defaultMinPrice} ${DEFAULT_CURRENCY_CODE} - ${defaultMaxPrice} ${DEFAULT_CURRENCY_CODE}`}</div>
        </div>
        <div className={styles.editPriceRangeBlockAction}>
          <InternalButton
            onClick={handleEditPriceRangeModalOpen}
            className={styles.editPriceRangeBtn}
          >
            <span className={styles.editPriceRangeEditIcon} />
          </InternalButton>
          {showEditPriceRangeModal && (
            <EditPriceRangeModal
              onSave={handlePriceRangeSave}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              defaultPrices={defaultPrices}
              closeModal={handleEditPriceRangeModalClose}
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderCategoriesBody = () => (
    <div className={styles.catalogListRow}>{catalogOptions.map(renderCatalogOption)}</div>
  );

  const renderCategoriesFooter = () => (
    <>
      <InternalButton onClick={closeModal} styleType="link" className={styles.link}>
        Cancel
      </InternalButton>
      <InternalButton
        isLoading={loading}
        onClick={() => {
          trackEvent({
            componentName: isPricelessShop ? PRICELESS_SHOP : SHOP_CREATION,
            elementPosition: MENU_CATALOG_SELECTION,
            target: "save",
            eventProperties: {
              categories_selected: catalogOptions
                .filter((menuItem) => getIsMenuItemSelected(menuItem))
                .map(({ name }) => name),
            },
          });
          saveShopCategories();
        }}
        styleType="primary"
        className={styles.btnSave}
        disabled={isEmpty(selectedTaxonIds)}
      >
        Save
      </InternalButton>
    </>
  );

  if (!isModal)
    return (
      <div className={styles.chooseCategoryBlock}>
        {isPricelessShop ? renderPricelessCategoriesHeader() : renderCategoriesHeader()}
        {renderCategoriesBody()}
      </div>
    );

  return (
    <Modal closeModal={closeModal} className={styles.categoriesModal}>
      <ModalDialog className={styles.modalDialog}>
        <ModalContent className={styles.modalContent}>
          <ModalHeader className={styles.modalHeader}>
            {renderCategoriesHeader()}
            <CloseButton onClick={closeModal} className={styles.closeIcon} />
          </ModalHeader>
          <ModalBody className={styles.modalBody}>{renderCategoriesBody()}</ModalBody>
          <ModalFooter className={styles.modalFooter}>{renderCategoriesFooter()}</ModalFooter>
        </ModalContent>
      </ModalDialog>
    </Modal>
  );
};

export default DummyStoreCategories;
